/** In-app Stock Brief destination for a ticker. Hash swaps the main column. */
export type BriefView = "chart" | "technicals" | "forecast" | "f13" | "congress";

export function stockBriefPath(symbol: string): string {
  return `/stocks/${encodeURIComponent(symbol.toUpperCase().replace(/\.US$/i, ""))}`;
}

export function parseBriefView(hash: string | null | undefined): BriefView {
  const h = (hash ?? "").replace(/^#/, "").toLowerCase();
  if (h === "technicals" || h === "forecast" || h === "f13" || h === "congress") return h;
  return "chart";
}

export function stockBriefHref(symbol: string, view: BriefView = "chart"): string {
  const base = stockBriefPath(symbol);
  return view === "chart" ? base : `${base}#${view}`;
}

/** Query param stamped on More-links so the destination can return here. */
export const STOCK_RETURN_PARAM = "from";
/** Ticker that originated the hop — dest/origin scopes are isolated. */
export const STOCK_VIA_PARAM = "via";

/** In-app path only — no scheme, no protocol-relative URL. */
export function parseStockReturn(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    /* keep raw */
  }
  if (!decoded.startsWith("/") || decoded.startsWith("//")) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(decoded)) return null;
  if (decoded.includes("\\")) return null;
  return decoded;
}

export function symbolFromStockPath(href: string): string | null {
  const path = href.split(/[?#]/)[0] ?? "";
  const m = path.match(/^\/stocks\/([^/]+)$/);
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1]).toUpperCase().replace(/\.US$/i, "");
  } catch {
    return m[1].toUpperCase().replace(/\.US$/i, "");
  }
}

export function stockReturnLabel(href: string, symbol?: string | null): string {
  const sym = (symbol || symbolFromStockPath(href) || "").trim().toUpperCase();
  return sym ? `Back to ${sym}` : "Back to details";
}

export function parseViaSymbol(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const sym = raw.trim().toUpperCase().replace(/\.US$/i, "");
  return /^[A-Z][A-Z0-9.-]{0,14}$/.test(sym) ? sym : null;
}

export function currentReturnHref(
  pathname: string,
  search: URLSearchParams,
  hash: string,
): string {
  const next = new URLSearchParams(search);
  next.delete(STOCK_RETURN_PARAM);
  next.delete(STOCK_VIA_PARAM);
  const q = next.toString();
  const h = hash ? `#${hash.replace(/^#/, "")}` : "";
  return `${pathname}${q ? `?${q}` : ""}${h}`;
}

export function withStockReturn(
  href: string,
  from: string,
  symbol?: string | null,
): string {
  if (!parseStockReturn(from)) return href;
  const hashIdx = href.indexOf("#");
  const base = hashIdx >= 0 ? href.slice(0, hashIdx) : href;
  const hash = hashIdx >= 0 ? href.slice(hashIdx) : "";
  const qIdx = base.indexOf("?");
  const path = qIdx >= 0 ? base.slice(0, qIdx) : base;
  const params = new URLSearchParams(qIdx >= 0 ? base.slice(qIdx + 1) : "");
  params.set(STOCK_RETURN_PARAM, from);
  const via = parseViaSymbol(symbol);
  if (via) params.set(STOCK_VIA_PARAM, via);
  const qs = params.toString();
  return `${path}${qs ? `?${qs}` : ""}${hash}`;
}
